import React, { useState, useEffect } from "react";
import { 
  Plus, 
  Search, 
  Layers, 
  Clock, 
  CheckCircle2, 
  FileCheck, 
  Cpu, 
  TrendingUp, 
  Calendar,
  DollarSign,
  Download,
  Boxes,
  Eye,
  Trash2,
  ChevronDown,
  ChevronUp,
  Workflow,
  Sparkles
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface PcbOrder {
  id: string;
  projectName: string;
  orderDate: string;
  baseMaterial: string;
  layers: number;
  width: number;
  height: number;
  qty: number;
  productType: string;
  thickness: number;
  color: string;
  silkscreen: string;
  materialType: string;
  surfaceFinish: string;
  copperWeight: string;
  innerCopperWeight: string;
  viaCovering: string;
  minViaSize: string;
  outlineTolerance: string;
  confirmFile: string;
  markOnPcb: string;
  testMethod: string;
  goldFingers: boolean;
  castellatedHoles: boolean;
  edgePlating: boolean;
  blindSlots: boolean;
  ulMarking: string;
  humidityCard: boolean;
  remark: string;
  fileName: string;
  fileSize: string;
  estimatedPrice: number;
  status: "جدید" | "جاری" | "در انتظار بررسی" | "تکمیل شده";
}

// Pre-populated default orders to make the app look complete and professional immediately
const DEFAULT_MOCK_ORDERS: PcbOrder[] = [
  {
    id: "PCB-904122",
    projectName: "STM32 Automation Controller Board",
    orderDate: "۱۴۰۵/۰۲/۲۸",
    baseMaterial: "FR-4",
    layers: 4,
    width: 85,
    height: 120,
    qty: 10,
    productType: "Industrial/Consumer electronics",
    thickness: 1.6,
    color: "Blue",
    silkscreen: "White",
    materialType: "FR4 TG135",
    surfaceFinish: "ENIG",
    copperWeight: "1OZ (35um)",
    innerCopperWeight: "1OZ (35um)",
    viaCovering: "Tented",
    minViaSize: "0.25mm/(0.35/0.4mm)",
    outlineTolerance: "±0.1mm(Precision)",
    confirmFile: "Yes",
    markOnPcb: "Remove Mark",
    testMethod: "Flying Probe Fully Test",
    goldFingers: false,
    castellatedHoles: true,
    edgePlating: false,
    blindSlots: false,
    ulMarking: "No",
    humidityCard: true,
    remark: "امپدانس ۵۰ اهم روی لاین‌های ترانسیور رعایت گردد.",
    fileName: "stm32_ctrl_v4_fab.zip",
    fileSize: "2.8 MB",
    estimatedPrice: 38.45,
    status: "جاری"
  },
  {
    id: "PCB-201948",
    projectName: "ESP32 Smart Home Node RF V3",
    orderDate: "۱۴۰۵/۰۲/۳۰",
    baseMaterial: "FR-4",
    layers: 2,
    width: 50,
    height: 50,
    qty: 5,
    productType: "Industrial/Consumer electronics",
    thickness: 1.2,
    color: "Green",
    silkscreen: "White",
    materialType: "FR4 TG135",
    surfaceFinish: "HASL (with lead)",
    copperWeight: "1OZ (35um)",
    innerCopperWeight: "1OZ (35um)",
    viaCovering: "Tented",
    minViaSize: "0.3mm/(0.4/0.45mm)",
    outlineTolerance: "±0.2mm(Regular)",
    confirmFile: "No",
    markOnPcb: "Remove Mark",
    testMethod: "Flying Probe Fully Test",
    goldFingers: false,
    castellatedHoles: false,
    edgePlating: false,
    blindSlots: false,
    ulMarking: "No",
    humidityCard: false,
    remark: "",
    fileName: "esp32_iot_smarthome_gerber.zip",
    fileSize: "1.1 MB",
    estimatedPrice: 5.0,
    status: "جدید"
  },
  {
    id: "PCB-784013",
    projectName: "LiPo Battery Charger 4S-40A BMS",
    orderDate: "۱۴۰۵/۰۲/۱۵",
    baseMaterial: "FR-4",
    layers: 2,
    width: 140,
    height: 90,
    qty: 50,
    productType: "Industrial/Consumer electronics",
    thickness: 1.6,
    color: "Black",
    silkscreen: "White",
    materialType: "FR4 TG135",
    surfaceFinish: "LeadFree HASL",
    copperWeight: "2OZ (70um)",
    innerCopperWeight: "1OZ (35um)",
    viaCovering: "Plugged",
    minViaSize: "0.3mm/(0.4/0.45mm)",
    outlineTolerance: "±0.2mm(Regular)",
    confirmFile: "No",
    markOnPcb: "2D barcode (Serial Number)",
    testMethod: "Flying Probe Fully Test",
    goldFingers: false,
    castellatedHoles: false,
    edgePlating: true,
    blindSlots: false,
    ulMarking: "Yes",
    humidityCard: false,
    remark: "ضخامت مس ۲ اونس برای جریان بالای شارژ باتری اجباری است.",
    fileName: "bms_4s_40a_high_power.zip",
    fileSize: "5.4 MB",
    estimatedPrice: 72.30,
    status: "تکمیل شده"
  }
];

interface PcbOrdersListProps {
  filterType?: "all" | "new" | "current";
}

export const PcbOrdersList: React.FC<PcbOrdersListProps> = ({ filterType = "all" }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<PcbOrder[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Load orders from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("pcb_orders");
    if (stored) {
      setOrders(JSON.parse(stored));
    } else {
      localStorage.setItem("pcb_orders", JSON.stringify(DEFAULT_MOCK_ORDERS));
      setOrders(DEFAULT_MOCK_ORDERS);
    }
  }, []);

  const toggleExpandOrder = (id: string) => {
    setExpandedOrderId(prev => prev === id ? null : id);
  };

  const handleDeleteOrder = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm("آیا از حذف این سفارش از لیست اطمینان دارید؟ این عمل غیرقابل بازگشت است.")) {
      const updated = orders.filter(o => o.id !== id);
      setOrders(updated);
      localStorage.setItem("pcb_orders", JSON.stringify(updated));
    }
  };

  // State workflow triggers
  const updateStatus = (id: string, nextStatus: PcbOrder["status"], e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = orders.map(order => 
      order.id === id ? { ...order, status: nextStatus } : order
    );
    setOrders(updated);
    localStorage.setItem("pcb_orders", JSON.stringify(updated));
  };

  // Filters based on search term and tab selection
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.projectName.toLowerCase().includes(searchTerm.toLowerCase()) || 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.baseMaterial.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterType === "new") {
      return order.status === "جدید" || order.status === "در انتظار بررسی";
    }
    if (filterType === "current") {
      return order.status === "جاری";
    }
    return true; // "all"
  });

  return (
    <div className="space-y-6 text-right font-sans" dir="rtl">
      
      {/* Header Utilities */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-100 dark:border-slate-700/60 shadow-sm">
        <div className="relative w-full md:w-96">
          <input
            type="text"
            className="w-full pl-3 pr-10 py-2 rounded-xl text-sm border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-800 dark:text-slate-100"
            placeholder="جستجوی نام پروژه، کد سفارش یا آلیاژ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search size={18} className="absolute right-3.5 top-2.5 text-slate-400" />
        </div>

        <button 
          onClick={() => navigate("/admin/orders/pcb/add")}
          className="w-full md:w-auto flex items-center justify-center gap-1.5 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl shadow-md transition-all shrink-0"
        >
          <Plus size={18} />
          <span>ثبت سفارش PCB جدید</span>
        </button>
      </div>

      {/* Grid containing orders cards */}
      {filteredOrders.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700/60 p-12 rounded-2xl text-center space-y-4 shadow-sm">
          <div className="w-16 h-16 bg-blue-50 dark:bg-blue-950/20 rounded-full flex items-center justify-center mx-auto text-blue-500">
            <Layers size={28} />
          </div>
          <div>
            <h4 className="font-bold text-slate-800 dark:text-white text-lg">سفارشی جهت نمایش یافت نشد</h4>
            <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">هیچ سفارش PCB متناسب با فیلترها در دیتابیس وجود ندارد.</p>
          </div>
          <button
            onClick={() => navigate("/admin/orders/pcb/add")}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-white text-xs font-semibold rounded-lg transition-all"
          >
            همین حالا اولین سفارش را بسازید
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => {
            const isExpanded = expandedOrderId === order.id;
            
            return (
              <div 
                key={order.id}
                className={`bg-white dark:bg-slate-800 border rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden ${
                  isExpanded ? "border-blue-500/50 dark:border-blue-500/40" : "border-slate-100 dark:border-slate-700/50"
                }`}
              >
                
                {/* Header Row clickable to expand */}
                <div 
                  onClick={() => toggleExpandOrder(order.id)}
                  className="p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 cursor-pointer select-none"
                >
                  <div className="flex items-start md:items-center gap-4">
                    <div className={`p-3 rounded-2xl shrink-0 ${
                      order.status === "تکمیل شده"
                        ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400"
                        : order.status === "جاری"
                          ? "bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 animate-pulse"
                          : "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400"
                    }`}>
                      <Layers size={24} />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-extrabold text-base text-slate-800 dark:text-white">{order.projectName}</h4>
                        <span className="text-[10px] font-mono bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded text-slate-500">{order.id}</span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-slate-400 dark:text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar size={13} />
                          {order.orderDate}
                        </span>
                        <span>•</span>
                        <span>ابعاد: {order.width}×{order.height} {order.baseMaterial}</span>
                        <span>•</span>
                        <span>تعداد: <span className="text-slate-700 dark:text-slate-200 font-bold">{order.qty} عدد</span></span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 w-full md:w-auto justify-end border-t border-slate-50 md:border-t-0 pt-3 md:pt-0">
                    <div className="text-left ml-4 font-mono">
                      <span className="text-lg font-black text-rose-600 dark:text-rose-400">${order.estimatedPrice}</span>
                      <span className="text-[9px] block text-slate-400">{(order.estimatedPrice * 62000).toLocaleString("fa-IR")} تومان</span>
                    </div>

                    {/* Status Pill */}
                    <span className={`text-[11px] font-bold px-3 py-1 rounded-full ${
                      order.status === "تکمیل شده"
                        ? "bg-emerald-100/75 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300"
                        : order.status === "جاری"
                          ? "bg-amber-100/75 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300"
                          : "bg-blue-100/75 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300"
                    }`}>
                      {order.status}
                    </span>

                    <div className="text-slate-400">
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                </div>

                {/* Expanded specifications detail sheets */}
                {isExpanded && (
                  <div className="bg-slate-50/50 dark:bg-slate-900/20 border-t border-slate-100 dark:border-slate-700/50 p-6 space-y-6">
                    
                    {/* Advanced Param Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-y-4 gap-x-6 text-xs bg-white dark:bg-slate-800/60 p-5 rounded-2xl border border-slate-100 dark:border-slate-800/80">
                      <div>
                        <span className="text-slate-400 block mb-0.5">آلیاژ شیت:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.baseMaterial}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">تعداد لایه‌ها:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.layers} لایه</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">ابعاد برد:</span>
                        <span className="font-bold font-mono text-slate-700 dark:text-slate-200">{order.width} * {order.height} میلی‌متر</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">تعداد کل:</span>
                        <span className="font-bold text-emerald-500">{order.qty} عدد</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">ضخامت مدار:</span>
                        <span className="font-bold font-mono text-slate-700 dark:text-slate-200">{order.thickness} mm</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">پوشش آبکاری پد:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.surfaceFinish}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">رنگ برد / سولدر:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.color}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">رنگ مارکاژ چاپ:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.silkscreen}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">ضخامت مس بیرونی:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.copperWeight}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">ضخامت مس داخلی:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.innerCopperWeight || "1OZ (35um)"}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">پوشش Via:</span>
                        <span className="font-bold text-slate-700 dark:text-slate-200">{order.viaCovering}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">حداقل ترَک / Via:</span>
                        <span className="font-bold font-mono text-slate-700 dark:text-slate-200">{order.minViaSize}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">نوع پکیج متریال:</span>
                        <span className="font-bold font-mono text-slate-700 dark:text-slate-200">{order.materialType}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">لوگو سازنده:</span>
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                          {order.markOnPcb === "Remove Mark" ? "حذف لوگو" : "چاپ معمولی تولید کننده"}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">کارت رطوبت‌سنج:</span>
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                          {order.humidityCard ? "دارد (تضمین انبارداری)" : "ندارد"}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">ویژگی خاص (مثال):</span>
                        <span className="font-medium text-amber-500">
                          {[
                            order.goldFingers && "انگشتی طلا",
                            order.castellatedHoles && "سوراخ‌های قلعه‌ای نیمه",
                            order.edgePlating && "آبکاری الکتریکی لبه",
                            order.blindSlots && "شیار کور سیگنال",
                          ].filter(Boolean).join(" ، ") || "هیچ‌کدام"}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-0.5">نام فایل گربر ساخت:</span>
                        <span className="font-mono text-[10px] text-blue-500 bg-blue-50 dark:bg-slate-900 border border-blue-100 dark:border-slate-800 rounded px-1.5 py-0.5 truncate max-w-full inline-block">
                          {order.fileName} ({order.fileSize})
                        </span>
                      </div>
                    </div>

                    {/* Order Remark Area if exists */}
                    {order.remark && (
                      <div className="bg-amber-50/50 dark:bg-amber-950/10 p-4 border border-amber-200/50 dark:border-amber-900/30 rounded-xl space-y-1">
                        <span className="text-xs font-bold text-amber-800 dark:text-amber-400">یادداشت فنی ثبت شده:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-sans">{order.remark}</p>
                      </div>
                    )}

                    {/* Administrative Status Modification Action Buttons */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-2">
                      <div className="flex gap-2 items-center text-xs text-slate-500 shrink-0">
                        <Workflow size={15} />
                        <span>گردش کار اداری برای مدیر سیستم:</span>
                      </div>
                      <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        <button
                          type="button"
                          onClick={(e) => updateStatus(order.id, "جدید", e)}
                          disabled={order.status === "جدید"}
                          className={`flex-1 sm:flex-none px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${
                            order.status === "جدید" 
                              ? "bg-blue-100 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 cursor-not-allowed border border-blue-200/40"
                              : "bg-white hover:bg-slate-100 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-300"
                          }`}
                        >
                          ثبت اولیه (جدید)
                        </button>
                        <button
                          type="button"
                          onClick={(e) => updateStatus(order.id, "جاری", e)}
                          disabled={order.status === "جاری"}
                          className={`flex-1 sm:flex-none px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${
                            order.status === "جاری" 
                              ? "bg-amber-100 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 cursor-not-allowed border border-amber-200/40"
                              : "bg-white hover:bg-slate-100 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-300"
                          }`}
                        >
                          تغییر وضعیت به جاری (تولید)
                        </button>
                        <button
                          type="button"
                          onClick={(e) => updateStatus(order.id, "تکمیل شده", e)}
                          disabled={order.status === "تکمیل شده"}
                          className={`flex-1 sm:flex-none px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${
                            order.status === "تکمیل شده" 
                              ? "bg-emerald-100 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 cursor-not-allowed border border-emerald-200/40"
                              : "bg-white hover:bg-slate-100 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-300"
                          }`}
                        >
                          علامت‌گذاری برای تحویل (تکمیل)
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteOrder(order.id, e)}
                          className="px-3 py-1.5 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 border border-transparent hover:border-red-200 dark:hover:border-red-900/30 text-slate-400 font-bold rounded-lg transition-all"
                          title="حذف سفارش"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};
